import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, GitBranch, Plus, RefreshCw, Loader2 } from 'lucide-react'
import { useDocumentTree, useRenewDocument } from '../../hooks/useDocumentTree'
import { MAIN_APP_URL } from '../../config'
import type { DocumentTreeNode } from '../../services/documentsApi'
import { LinkDocumentPanel } from './LinkDocumentModal'

interface DocumentTreeProps {
  koblinkId: string
  actorId?: string
}

const relationColors: Record<string, string> = {
  amendment: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  renewal: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  related: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
}

export function DocumentTree({ koblinkId, actorId }: DocumentTreeProps) {
  const { t } = useTranslation()
  const { data: tree, isLoading, error } = useDocumentTree(koblinkId, actorId)
  const renewMutation = useRenewDocument()
  const [showLinkPanel, setShowLinkPanel] = useState(false)
  const [renewSuccess, setRenewSuccess] = useState<string | null>(null)

  const handleRenew = () => {
    renewMutation.mutate(
      { koblinkId, actorId },
      {
        onSuccess: (doc) => {
          setRenewSuccess(doc.koblink_id)
        },
      },
    )
  }

  if (isLoading) {
    return <p className="text-[11px] text-neutral-400 py-2 text-center">{t('common.loading')}</p>
  }

  if (error) {
    return <p className="text-[11px] text-neutral-400 py-2 text-center">{t('related.noRelated')}</p>
  }

  const hasTree = tree && (tree.children.length > 0 || tree.relation_type)

  return (
    <div>
      {hasTree ? (
        <div className="space-y-0.5">
          <TreeNode node={tree} depth={0} />
        </div>
      ) : (
        <p className="text-[11px] text-neutral-400 py-3 text-center">{t('related.noRelated')}</p>
      )}

      {/* Renewal success */}
      {renewSuccess && (
        <div className="mt-2 p-2 rounded bg-success-50 dark:bg-success-900/20 text-[11px]">
          <p className="text-success-700 dark:text-success-400 font-medium">{t('related.renewSuccess')}</p>
          <a
            href={`${MAIN_APP_URL}/documents/${renewSuccess}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-600 mt-1"
          >
            <ExternalLink size={10} />
            {t('status.viewInKobSign')}
          </a>
        </div>
      )}

      {/* Actions */}
      {!showLinkPanel && (
        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-neutral-100 dark:border-neutral-700">
          <button
            onClick={() => setShowLinkPanel(true)}
            className="inline-flex items-center gap-1 text-[11px] text-primary-500 hover:text-primary-600 transition-colors"
          >
            <Plus size={10} />
            {t('related.linkDocument')}
          </button>
          <button
            onClick={handleRenew}
            disabled={renewMutation.isPending}
            className="inline-flex items-center gap-1 text-[11px] text-primary-500 hover:text-primary-600 disabled:opacity-50 transition-colors"
          >
            {renewMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            {t('related.renewDocument')}
          </button>
        </div>
      )}

      {/* Link document panel */}
      {showLinkPanel && (
        <LinkDocumentPanel
          koblinkId={koblinkId}
          actorId={actorId}
          onClose={() => setShowLinkPanel(false)}
        />
      )}
    </div>
  )
}

function TreeNode({ node, depth }: { node: DocumentTreeNode; depth: number }) {
  const { t } = useTranslation()

  return (
    <div style={{ paddingLeft: depth * 12 }}>
      <div
        className={`flex items-center gap-1.5 py-1 px-1.5 rounded ${
          node.is_current
            ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
            : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
        }`}
      >
        <GitBranch size={10} className={node.is_current ? 'text-primary-500' : 'text-neutral-400'} />

        {node.relation_type && (
          <span className={`text-[9px] px-1 py-0.5 rounded shrink-0 ${relationColors[node.relation_type] || relationColors.related}`}>
            {t(`related.relationType.${node.relation_type}`)}
          </span>
        )}

        <span
          className={`text-[11px] truncate flex-1 ${
            node.is_current
              ? 'text-primary-700 dark:text-primary-300 font-medium'
              : 'text-neutral-700 dark:text-neutral-300'
          }`}
        >
          {node.title}
        </span>

        {node.completed_at && (
          <span className="text-[9px] text-neutral-400 shrink-0">
            {new Date(node.completed_at).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
          </span>
        )}

        {!node.is_current && (
          <a
            href={`${MAIN_APP_URL}/documents/${node.koblink_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-neutral-400 hover:text-primary-500 shrink-0"
          >
            <ExternalLink size={10} />
          </a>
        )}
      </div>

      {node.children.map((child) => (
        <TreeNode key={child.koblink_id} node={child} depth={depth + 1} />
      ))}
    </div>
  )
}
